import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { sentryCaptureException } from '../../utilities';
import { defaultDoc } from '../share/defaultDoc';

export interface PreviousTaskComments {
  readonly pkUuid: string;
  readonly pkTaskWiseAuditComments: number;
  readonly fkAssetInspectionTask: number;
  readonly inspectorId?: number;
  readonly inspectorRoleId: number;
  readonly comment: string;
  readonly dateCreated: string;
  readonly dateModified: string;
  readonly createdBy: string;
  readonly isActive: boolean;
  readonly attentionFlag: boolean;
  readonly fkClerk?: number;
  readonly clerkId?: number;
}

export const previousTaskCommentsSchema: RxJsonSchema<PreviousTaskComments> = {
  type: 'object',
  version: 2,
  description: 'describes previous task comments',
  primaryKey: 'pkUuid',
  properties: {
    pkUuid: { type: 'string', maxLength: 36 },
    pkTaskWiseAuditComments: { type: ['number', 'null'] },
    fkAssetInspectionTask: { type: ['number', 'null'] },
    inspectorId: { type: ['number', 'null'] },
    fkClerk: { type: ['number', 'null'] },
    clerkId: { type: ['number', 'null'] },
    inspectorRoleId: { type: ['number', 'null'] },
    comment: { type: ['string', 'null'] },
    dateCreated: { type: 'string', maxLength: 36 },
    dateModified: { type: ['string', 'null'] },
    createdBy: { type: ['string', 'null'] },
    isActive: { type: ['boolean', 'null'] },
    attentionFlag: { type: ['boolean', 'null'] },
  },
  indexes: ['dateCreated'],
};

export type PreviousTaskCommentsCollection = RxCollection<PreviousTaskComments> | null;
export type PreviousTaskCommentsDocument = RxDocument<PreviousTaskComments>;

export const previousTaskCommentsPullQueryBuilder = (document: PreviousTaskComments) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = {
      ...defaultDoc,
      dateModified: '1900-01-01T01:01:01.1234567-06:00',
    };
  }

  const query = print(gql`
    query mobileTaskWiseAuditComments($lastRequested: DateTime!) {
      mobileTaskWiseAuditComments(lastRequested: $lastRequested) {
        pkTaskWiseAuditComments
        fkAssetInspectionTask
        inspectorId
        fkClerk
        inspectorRoleId
        comment
        dateCreated
        dateModified
        createdBy
        pkUuid
        isActive
        attentionFlag
      }
    }
  `);

  return {
    query,
    variables: {
      lastRequested: doc?.dateModified ? doc?.dateModified : doc?.dateCreated,
    },
  };
};

export const previousTaskCommentsPullModifier = (doc: PreviousTaskComments) => {
  const result = {
    ...doc,
  };
  try {
    if (result.dateModified !== null) {
      const dateCreated = new Date(result.dateCreated);
      const dateModified = new Date(result.dateModified);
      if (dateCreated > dateModified) {
        {
          result.dateModified = result.dateCreated;
        }
      }
    } else {
      result.dateModified = result.dateCreated;
    }
    if (result.fkClerk) {
      result.clerkId = result.fkClerk;
    }
  } catch (error) {
    console.log('Error previous comments pull modifier ', error); // eslint-disable-line no-console
    // console logs will be removed once sentry implementation is done
    sentryCaptureException(
      error,
      '~ file: queryBuilder.tsx ~ line 95 ~ previousTaskCommentsPullModifier ~',
    );
  }
  return result;
};

export const previousTaskCommentsMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: PreviousTaskCommentsDocument) => {
    return oldDoc;
  },
  2: (oldDoc: PreviousTaskCommentsDocument) => {
    const newDoc = {
      ...oldDoc,
      fkClerk: oldDoc.fkClerk || null,
      clerkId: oldDoc.clerkId || null,
    };
    return newDoc;
  },
};

export default {};
